import moment from 'moment'
import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { BaseComponentWithContextProps, componentWithContext } from '../../../../../base/customHooks/componentHOC'
import IPModal from '../../../../../components/IPModal'
import IPSelectBox, { ISelectOption } from '../../../../../components/IPSelectBox/IPSelectBox'
import Table from '../../../../../components/Table'
import { GetVideoWatchReportApi } from '../services/getVideoWatchReportApi'
import { IVideoWatchReport } from '../models/responseModels'
import { parseScormData, parseQuizAttempts, formatSeconds, IScormInteraction } from '../utils/parseScormData'

function WatchReport(props: BaseComponentWithContextProps) {
  const [report, setReport] = useState<IVideoWatchReport[]>([])
  const [videoFilter, setVideoFilter] = useState<ISelectOption | null>(null)
  const [onlyCompleted, setOnlyCompleted] = useState(false)
  const [detailRow, setDetailRow] = useState<IVideoWatchReport | null>(null)

  useEffect(() => {
    getReport()
  }, [])

  const getReport = async () => {
    props.showLoading()
    const api = new GetVideoWatchReportApi({})
    api.getWatchReport().then(res => {
      setReport(res)
      props.hideLoading()
    })
  }

  const videoOptions = useMemo(() => {
    const uniqueVideos = new Map<number, string>()
    report.forEach(r => uniqueVideos.set(r.VideoId, r.VideoTitle))
    return Array.from(uniqueVideos.entries()).map(([value, label]) => ({ value, label }))
  }, [report])

  const filteredReport = useMemo(
    () =>
      report
        .filter(r => !videoFilter || r.VideoId === videoFilter.value)
        .filter(r => !onlyCompleted || r.Completed),
    [report, videoFilter, onlyCompleted]
  )

  const tableColumns = useMemo(
    () => [
      { Header: 'Kategori', accessor: 'category' },
      { Header: 'Video', accessor: 'video' },
      { Header: 'Kullanıcı', accessor: 'user' },
      { Header: 'Bayi Kodu', accessor: 'dealer' },
      { Header: 'İzlenme Yüzdesi', accessor: 'percent' },
      { Header: 'Durum', accessor: 'status' },
      { Header: 'Son İzleme', accessor: 'lastWatched' },
      { Header: 'Tamamlanma Tarihi', accessor: 'completedDate' },
      { Header: 'Detay', accessor: 'detail' },
    ],
    []
  )

  const tableData = useMemo(
    () =>
      filteredReport.map((item, index) => ({
        category: item.VideoCategoryName,
        video: item.VideoTitle,
        user: item.UserFullName,
        dealer: item.DealerSapCode,
        percent: `%${Math.round(item.Completed ? 100 : item.WatchPercent)}`,
        status: item.Completed ? 'Tamamlandı' : 'Devam Ediyor',
        lastWatched: item.LastWatchedDate ? moment(item.LastWatchedDate).format('DD.MM.YYYY HH:mm') : '',
        completedDate: item.CompletedDate ? moment(item.CompletedDate).format('DD.MM.YYYY HH:mm') : '-',
        detail: item.ScormCmiData ? (
          <a role='button' style={{ cursor: 'pointer', color: '#007bff' }} onClick={() => setDetailRow(item)}>
            İncele
          </a>
        ) : (
          <span style={{ color: '#ccc' }}>-</span>
        ),
      })),
    [filteredReport]
  )

  const detail = useMemo(() => (detailRow ? parseScormData(detailRow.ScormCmiData) : null), [detailRow])
  const attempts = useMemo(() => (detailRow ? parseQuizAttempts(detailRow.ScormQuizAttempts) : []), [detailRow])

  const renderInteractions = (interactions: IScormInteraction[]) => (
    <table className='table table-bordered table-striped'>
      <thead>
        <tr>
          <th>#</th>
          <th>Soru Id</th>
          <th>Tip</th>
          <th>Verilen Cevap</th>
          <th>Doğru Cevap</th>
          <th>Sonuç</th>
        </tr>
      </thead>
      <tbody>
        {interactions.map(it => (
          <tr key={it.index}>
            <td>{it.index + 1}</td>
            <td>{it.id || '-'}</td>
            <td>{it.type || '-'}</td>
            <td>{it.response || '-'}</td>
            <td>{it.correctResponse || '-'}</td>
            <td>
              {it.result === 'correct' ? (
                <span style={{ color: '#4caf50' }}>Doğru</span>
              ) : it.result === 'wrong' || it.result === 'incorrect' ? (
                <span style={{ color: '#e53935' }}>Yanlış</span>
              ) : (
                it.result || '-'
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )

  return (
    <div className='container'>
      <div className='content col-lg-12 m-auto'>
        <div className='card'>
          <div className='card-header'>
            <div className='row'>
              <div className='mb-3'>
                <span className='h4'>Video İzlenme Raporu</span>
                <Link to='/admin/HomeAndSiteOperation/video/list'>
                  <button type='button' className='btn btn-secondary' style={{ float: 'right' }}>
                    Videolara Dön
                  </button>
                </Link>
              </div>
            </div>
          </div>
          <div className='card-body'>
            <div className='row mb-4'>
              <div className='col-lg-4'>
                <label className='labeltext'>Video</label>
                <IPSelectBox
                  id='videoFilter'
                  name='videoFilter'
                  isClearable
                  options={videoOptions}
                  value={videoFilter}
                  onChangeSingle={option => setVideoFilter(option)}
                />
              </div>
              <div className='col-lg-4 d-flex align-items-end'>
                <div className='form-check'>
                  <input
                    type='checkbox'
                    className='form-check-input'
                    id='onlyCompleted'
                    checked={onlyCompleted}
                    onChange={e => setOnlyCompleted(e.target.checked)}
                  />
                  <label className='form-check-label' htmlFor='onlyCompleted'>
                    Sadece Tamamlananlar
                  </label>
                </div>
              </div>
            </div>
            <div className='table-responsive'>
              <Table columns={tableColumns} data={tableData} currentPage={0} />
            </div>
          </div>
        </div>
      </div>

      <IPModal
        show={!!detailRow}
        hideConfirm={true}
        width={900}
        title={detailRow ? `${detailRow.VideoTitle} — ${detailRow.UserFullName}` : ''}
        onClose={() => setDetailRow(null)}
      >
        <div>
          {detail ? (
            <>
              <div className='row mb-3'>
                <div className='col-lg-4'>
                  <strong>Durum:</strong> {detail.lessonStatus || '-'}
                </div>
                <div className='col-lg-4'>
                  <strong>Toplam İzleme Süresi:</strong>{' '}
                  {detailRow && detailRow.TotalWatchedSeconds > 0 ? formatSeconds(detailRow.TotalWatchedSeconds) : detail.duration || '-'}
                </div>
                <div className='col-lg-4'>
                  <strong>Quiz Denemesi:</strong> {attempts.length}
                </div>
              </div>

              {attempts.length > 0 ? (
                attempts.map(att => (
                  <div key={att.attemptNo} className='mb-4' style={{ border: '1px solid #eee', borderRadius: '8px', padding: '12px' }}>
                    <div className='mb-2'>
                      <strong>Deneme {att.attemptNo}</strong>
                      {att.date && <span style={{ color: '#999', marginLeft: 10 }}>{moment(att.date).format('DD.MM.YYYY HH:mm')}</span>}
                      {att.data && (
                        <span style={{ marginLeft: 15 }}>
                          <strong>Puan:</strong>{' '}
                          {att.data.scoreRaw
                            ? `${att.data.scoreRaw}${att.data.scoreMax ? ' / ' + att.data.scoreMax : ''}`
                            : att.data.scoreScaled
                              ? `%${Math.round(Number(att.data.scoreScaled) * 100)}`
                              : '-'}
                          {att.data.lessonStatus ? ` (${att.data.lessonStatus})` : ''}
                        </span>
                      )}
                    </div>
                    {att.data && att.data.interactions.length > 0 ? (
                      renderInteractions(att.data.interactions)
                    ) : (
                      <div style={{ color: '#999' }}>Bu denemede soru/cevap detayı bildirilmemiş.</div>
                    )}
                  </div>
                ))
              ) : detail.interactions.length > 0 ? (
                renderInteractions(detail.interactions)
              ) : (
                <div className='text-center' style={{ padding: '20px', color: '#999' }}>
                  Bu içerik için kayıtlı quiz/soru-cevap bilgisi bulunmuyor. (Paket quiz sonucu bildirmemiş.)
                </div>
              )}
            </>
          ) : (
            <div className='text-center' style={{ padding: '20px', color: '#999' }}>
              Detay verisi bulunamadı.
            </div>
          )}
        </div>
      </IPModal>
    </div>
  )
}
export default componentWithContext(WatchReport)
